"use client";

import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  BaseEditor,
  Descendant,
  Editor,
  Node,
  Range,
  Element as SlateElement,
  Transforms,
  createEditor,
} from "slate";
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  Slate,
  useSlate,
  withReact,
} from "slate-react";
import { twMerge } from "tailwind-merge";

type CustomText = { text: string };
type PrefixElement = { type: "prefix"; children: CustomText[] };
type ParagraphElement = {
  type: "paragraph";
  children: (CustomText | PrefixElement)[];
};
type CustomElement = PrefixElement | ParagraphElement;

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}
interface EditableTextAreaProps {
  initialText?: string;
  onChange?: (text: string) => void;
  prefixNode?: ReactNode;
  onPrefixRemove?: () => void;
  autoFocus?: boolean;
  focusTrigger?: unknown;
  placeholder?: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function EditableTextArea({
  initialText = "",
  onChange,
  prefixNode,
  onPrefixRemove,
  autoFocus = false,
  focusTrigger,
  placeholder,
  onKeyDown,
  disabled = false,
  className,
  style,
}: EditableTextAreaProps) {
  const editor = useMemo(() => {
    const e = withReact(createEditor());

    const { isInline, isVoid } = e;
    // prefix 使用 inline void，遵循 Slate 官方推荐的渲染方式，保证换行时光标可正确移动
    e.isInline = (el) => (el.type === "prefix" ? true : isInline(el));
    e.isVoid = (el) => (el.type === "prefix" ? true : isVoid(el));

    return e;
  }, []);

  const prevPrefixRef = useRef<ReactNode>(prefixNode);
  const hasAutoFocusedRef = useRef(false);

  // initialValue 只在首次渲染时使用
  const initialValue = useMemo<Descendant[]>(
    () => [
      {
        type: "paragraph",
        children: [{ text: initialText }],
      } as ParagraphElement,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 处理 prefixNode 变化和聚焦
  useEffect(() => {
    const previousPrefix = prevPrefixRef.current;
    const prefixChanged = previousPrefix !== prefixNode;
    const shouldFocus =
      (!hasAutoFocusedRef.current && autoFocus) || prefixChanged;

    // 1. 安全获取第一个段落
    const firstParagraph = editor.children[0];
    if (
      !SlateElement.isElement(firstParagraph) ||
      firstParagraph.type !== "paragraph"
    ) {
      return;
    }

    // 2. 批量更新节点和选区（减少 onChange 触发次数）
    Editor.withoutNormalizing(editor, () => {
      // 清空所有现有的 prefix 节点（倒序删除避免索引问题）
      for (let i = firstParagraph.children.length - 1; i >= 0; i--) {
        const child = firstParagraph.children[i];
        if (SlateElement.isElement(child) && child.type === "prefix") {
          Transforms.removeNodes(editor, { at: [0, i] });
        }
      }

      // 如果 prefixNode 有值，添加一个新的 prefix 到开头
      if (prefixNode) {
        Transforms.insertNodes(
          editor,
          { type: "prefix", children: [{ text: "" }] } as PrefixElement,
          { at: [0, 0] }
        );
      }

      // 3. 在同一批次中设置选区（如果需要聚焦）
      if (shouldFocus) {
        // 将光标设置到段落末尾
        const targetPoint = Editor.end(editor, [0]);
        Transforms.select(editor, targetPoint);
      }
    });

    prevPrefixRef.current = prefixNode;
    hasAutoFocusedRef.current = hasAutoFocusedRef.current || autoFocus;

    // 4. 如果需要聚焦，在操作完成后聚焦编辑器
    if (shouldFocus) {
      requestAnimationFrame(() => {
        ReactEditor.focus(editor);
      });
    }
  }, [prefixNode, editor, autoFocus]);

  // 处理 initialText 变化（按照 Slate 官方推荐：直接修改 editor.children）
  useEffect(() => {
    if (!initialText) return;

    // 获取当前文本内容（排除 prefix）
    const currentText = editor.children
      .map((n) => {
        if (SlateElement.isElement(n) && n.type === "paragraph") {
          return n.children
            .filter((c) => !SlateElement.isElement(c) || c.type !== "prefix")
            .map((c) => Node.string(c))
            .join("");
        }
        return Node.string(n);
      })
      .join("\n");

    // 只在编辑器为空时更新（避免覆盖用户输入）
    if (!currentText) {
      // Slate 官方推荐：直接修改 editor.children
      editor.children = [
        {
          type: "paragraph",
          children: [{ text: initialText }],
        } as ParagraphElement,
      ];

      // 设置光标到末尾（在 onChange 之前）
      const endPoint = { path: [0, 0], offset: initialText.length };
      editor.selection = {
        anchor: endPoint,
        focus: endPoint,
      };

      // 触发重新渲染
      editor.onChange();

      // 如果需要自动聚焦
      if (autoFocus && !hasAutoFocusedRef.current) {
        requestAnimationFrame(() => {
          ReactEditor.focus(editor);
          hasAutoFocusedRef.current = true;
        });
      }
    }
  }, [initialText, editor, autoFocus]);

  // 处理外部触发的聚焦（如 attachmentList 变化）
  useEffect(() => {
    if (focusTrigger !== undefined) {
      requestAnimationFrame(() => {
        ReactEditor.focus(editor);
        // 将光标移动到文本末尾
        const targetPoint = Editor.end(editor, [0]);
        Transforms.select(editor, targetPoint);
      });
    }
  }, [focusTrigger, editor]);

  const getPlainText = useCallback((nodes: Descendant[]) => {
    return nodes
      .map((node) => {
        // 对于 paragraph 节点，过滤掉 prefix 子节点
        if (SlateElement.isElement(node) && node.type === "paragraph") {
          const textChildren = node.children.filter(
            (child) => !SlateElement.isElement(child) || child.type !== "prefix"
          );
          return textChildren.map((child) => Node.string(child)).join("");
        }
        return Node.string(node);
      })
      .join("\n");
  }, []);

  const handleChange = useCallback(
    (nextValue: Descendant[]) => {
      // 检测用户是否删除了 prefix
      if (prefixNode && onPrefixRemove) {
        const firstParagraph = nextValue[0];
        if (
          SlateElement.isElement(firstParagraph) &&
          firstParagraph.type === "paragraph"
        ) {
          const hasPrefixInEditor = firstParagraph.children.some(
            (child) => SlateElement.isElement(child) && child.type === "prefix"
          );

          // 如果应该有 prefix 但编辑器中没有，说明用户删除了
          if (!hasPrefixInEditor) {
            onPrefixRemove();
          }
        }
      }

      onChange?.(getPlainText(nextValue));
    },
    [getPlainText, onChange, prefixNode, onPrefixRemove]
  );

  return (
    <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
      <EditableContent
        prefixNode={prefixNode}
        placeholder={placeholder}
        className={twMerge(
          "text-white text-sm outline-none h-full leading-[30px]",
          className
        )}
        style={style}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
    </Slate>
  );
}

interface EditableContentProps {
  prefixNode?: ReactNode;
  placeholder?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}

function EditableContent({
  prefixNode,
  placeholder,
  className,
  style,
  onKeyDown,
  disabled = false,
}: EditableContentProps) {
  const editor = useSlate();

  const renderElement = useCallback(
    ({ attributes, children, element }: RenderElementProps) => {
      switch (element.type) {
        case "prefix":
          return (
            <span
              {...attributes}
              contentEditable={false}
              className="inline-flex mr-2 align-middle"
            >
              {prefixNode}
              {children}
            </span>
          );
        case "paragraph": {
          // 检查是否需要显示 placeholder
          // 条件：1. 只有一个段落  2. 是第一个段落  3. 第一个段落为空
          const isOnlyOneParagraph = editor.children.length === 1;
          const isFirstParagraph = editor.children[0] === element;
          const textChildren = element.children.filter(
            (child) => !SlateElement.isElement(child) || child.type !== "prefix"
          );
          const hasText = textChildren.some(
            (child) => Node.string(child).length > 0
          );

          const shouldShowPlaceholder =
            isOnlyOneParagraph && isFirstParagraph && !hasText && placeholder;

          return (
            <div {...attributes} className="whitespace-pre-wrap relative">
              {children}
              {shouldShowPlaceholder && (
                <div
                  contentEditable={false}
                  className="absolute top-0 left-0 pointer-events-none select-none"
                >
                  {/* 用一个 invisible 的元素占位，确保 placeholder 跟在 prefix 后面 */}
                  {prefixNode && (
                    <span className="inline-flex mr-2 align-middle invisible">
                      {prefixNode}
                    </span>
                  )}
                  {placeholder}
                </div>
              )}
            </div>
          );
        }
        default:
          return <span {...attributes}>{children}</span>;
      }
    },
    [editor, prefixNode, placeholder]
  );

  const handleKeyDownInternal = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      // 如果正在使用输入法（IME）进行组合输入，不要处理 Enter 键
      // 让浏览器和输入法自己处理，避免光标位置错误
      if (ReactEditor.isComposing(editor)) {
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        if (editor.selection && Range.isCollapsed(editor.selection)) {
          Editor.insertText(editor, "\n");
        } else {
          Editor.insertBreak(editor);
        }
      }
    },
    [editor, onKeyDown]
  );

  return (
    <Editable
      className={className}
      style={style}
      renderElement={renderElement}
      onKeyDown={handleKeyDownInternal}
      readOnly={disabled}
    />
  );
}
